export type AgentOption = {
    /**
   * This is the name of the Agent (defined in the BE)
   */
    appName: string;
    label: string;
    description: string;
};

/** Leaf prompt in a `one_layer` template. */
export type AgentTemplatePrompt = {
    title: string;
    prompt: string;
};

/** Node in a `two_layer` template (group or leaf). */
export type AgentTemplateMember = {
    title: string;
    prompt?: string;
    members?: AgentTemplateMember[];
};

/** Template group from `GET /api/v1/agents-metadata` (per agent). */
export type AgentTemplateOneLayer = {
    name: string;
    title: string;
    template_type: "one_layer";
    prompts: AgentTemplatePrompt[] | string[]; // string[] is for the old templates
};

export type AgentTemplateTwoLayer = {
    name: string;
    title: string;
    template_type: "two_layer";
    members: AgentTemplateMember[];
};

export type AgentTemplate = AgentTemplateOneLayer | AgentTemplateTwoLayer;

/** Response item from the agent chat backend `GET /api/v1/agents-metadata`. */
export type AgentMetadata = {
    app_name: string;
    display_name: string;
    description: string;
    templates?: AgentTemplate[];
    input_placeholder: string;
    sort_order: number;
    enabled: boolean;
    active_databags: number[];
};

/** Session state key written by tail-spend consolidation tools (ADK state_delta). */
export const CONSOLIDATION_PROGRESS_STATE_KEY =
    "temp:tail_spend_consolidation_progress" as const;

export type ConsolidationToolProgress = {
    stage: string;
    completed: number;
    total: number;
    detail?: string;
    /** 1–4 macro phase from backend (opportunities → initiatives → persist → complete). */
    stageNumber?: number;
    stageTotal?: number;
};

/**
 * Embedded Superset dashboard metadata emitted by dashgen tool results
 * (`functionResponse.response.embedded`). Guest tokens are fetched separately
 * from `GET /api/v1/superset/guest-token?dashboard_uuid={uuid}`.
 */
export type EmbeddedDashboard = {
    /** Superset embedded-dashboard uuid (NOT the numeric dashboard id). */
    uuid: string;
    /** Browser-facing Superset origin the iframe loads from. */
    superset_domain: string;
    dashboard_id: number;
    title: string;
};

/** Row from `GET /api/v1/superset/dashboards` — a dashgen dashboard owned by the user. */
export type UserDashboardRecord = {
    dashboard_id: number;
    title: string | null;
    url: string | null;
    embedded_uuid: string | null;
    /** Originating chat session — deep-link target for opening the dashboard. */
    session_id: string;
    app_name: string;
    databag_id: number;
    created_at: string;
    updated_at: string;
};

/**
 * EmbeddedDashboard as shown in the side panel. `refreshToken` bumps whenever
 * the agent creates/edits the dashboard (its embed uuid stays stable across
 * edits), forcing the iframe to remount and show the new charts.
 */
export type EmbeddedDashboardView = EmbeddedDashboard & {
    refreshToken: number;
};

export type ChatMessage = {
    id: string;
    type: "user" | "bot";
    content: string;
    event_type?: "text" | "function_call" | "function_response";
    /** ADK invocation id for the event this bubble came from (for feedback API). */
    invocationId?: string;
    timestamp: Date;
    isTransitioning?: boolean;
    previousContent?: string;
    previousEventType?: "function_call" | "function_response";
    /** Populated from ADK state_delta during tail-spend consolidation. */
    toolProgress?: ConsolidationToolProgress;
    /** Superset dashboard produced by this tool response (dashgen). */
    embedded?: EmbeddedDashboard;
};

export type ChatSession = {
    id: string;
    userId: string;
    name: string;
    messages: ChatMessage[];
    created_at: Date;
    updated_at: Date;
    appName: string;
    isWaitingForResponse?: boolean;
};

// Backend session adapter response type
export type BackendChatSession = {
    session_id: string;
    user_id: string;
    last_update_time: string;
    databag_id: number;
    display_name: string;
    app_name: string;
};

// ADK Event types

export type ADKContentPart = {
    text?: string;
    thought?: boolean;
    functionCall?: {name: string; args: Record<string, any>};
    functionResponse?: {name: string; response: any};
};

export type ADKEvent = {
    id: string;
    author: string;
    invocationId?: string;
    /** Wire JSON often uses snake_case */
    invocation_id?: string;
    content?: {role: string; parts: ADKContentPart[]};
    actions?: {
        stateDelta?: Record<string, any>;
        artifactDelta?: Record<string, number>;
    };
    partial?: boolean;
    timestamp?: number;
};

export type ADKSession = {
    id: string;
    app_name: string;
    user_id: string;
    state: Record<string, any>;
    events: ADKEvent[];
    last_update_time: number;
};
